import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Sortable from 'sortablejs';
import type { Category, CategoryAction } from 'markcat-domain';
import { ContextMenu } from './ContextMenu';
import { Modal } from './Modal';
import { ChevronRightIcon, EditIcon, CloseIcon, SearchIcon, PlusIcon, BookmarkIcon } from './icons';
import { SidebarSection } from './SidebarSection';
import styles from './CategoryTree.module.css';

// Shared Sortable config for the root list and every children container.
// On touch, a drag starts only after a 350ms hold (delayOnTouchOnly) and a
// swipe that moves >5px inside that window cancels it — so touch-scrolling
// the tree never reparents a category; tap still selects, long-press drags.
function createCategorySortable(el: HTMLElement, onCategoryAction: (action: CategoryAction) => void) {
  return Sortable.create(el, {
    animation: 150,
    handle: `.${styles.item}`,
    draggable: `.${styles.node}`,
    group: 'categories',
    swapThreshold: 0.65,
    invertSwap: true,
    delay: 350,
    delayOnTouchOnly: true,
    touchStartThreshold: 5,
    onChoose() {
      // Haptic tick when the drag arms — Android only; iOS has no vibrate API.
      navigator.vibrate?.(10);
    },
    onEnd(evt) {
      // Same-container drag = sibling reorder, which the alphabetical tree
      // doesn't support. Always revert the DOM; only mutate on reparenting.
      const movedPath = evt.item.dataset.path;
      const targetParent = evt.to.dataset.parentPath ?? null;
      evt.from.insertBefore(evt.item, evt.from.children[evt.oldIndex!] || null);
      if (movedPath && evt.to !== evt.from && movedPath !== targetParent) {
        onCategoryAction({ type: 'move', path: movedPath, name: '', targetParent });
      }
    },
  });
}

interface CategoryTreeProps {
  categories: Category[];
  selectedPath: string | null;
  allSelected: boolean;
  onSelect: (path: string | null) => void;
  onCategoryAction?: (action: CategoryAction) => void;
  onRecategorize?: (bookmarkIds: string[], targetPath: string) => void;
}

export function CategoryTree({ categories, selectedPath, allSelected, onSelect, onCategoryAction, onRecategorize }: CategoryTreeProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editingPath, setEditingPath] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // A heading action unfolds a folded section until the user folds it again;
  // tying the unfold to the add row would re-fold it under a blur-commit.
  const [actionOpened, setActionOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchToggleRef = useRef<HTMLButtonElement>(null);
  const query = searchQuery.trim().toLocaleLowerCase();
  const searching = query.length > 0;
  const visibleCategories = useMemo(() => {
    const filter = (nodes: Category[]): Category[] => nodes.flatMap(category => {
      if (category.name.toLocaleLowerCase().includes(query)) return [category];
      const children = filter(category.children);
      return children.length ? [{ ...category, children }] : [];
    });
    return searching ? filter(categories) : categories;
  }, [categories, query, searching]);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; path: string; name: string; count: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ path: string; name: string; count: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const lastTouchRef = useRef(0);

  const toggleCollapse = useCallback((path: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // Drag-reorder: top-level categories
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !onCategoryAction || searching) return;
    const sortable = createCategorySortable(el, onCategoryAction);
    return () => sortable.destroy();
  }, [onCategoryAction, searching]);

  const handleRename = useCallback(
    (path: string, newName: string) => {
      setEditingPath(null);
      if (newName.trim() && onCategoryAction) {
        onCategoryAction({ type: 'rename', path, name: newName.trim() });
      }
    },
    [onCategoryAction],
  );

  const handleContextMenu = useCallback((e: React.MouseEvent, path: string, name: string, count: number) => {
    e.preventDefault();
    e.stopPropagation();
    // On touch, long-press starts a Sortable drag; swallow the contextmenu
    // Android fires from the same gesture. Desktop right-click keeps the menu.
    if (Date.now() - lastTouchRef.current < 1000) return;
    setContextMenu({ x: e.clientX, y: e.clientY, path, name, count });
  }, []);

  const requestDelete = useCallback((path: string, name: string, count: number) => {
    if (count === 0) {
      onCategoryAction?.({ type: 'delete', path, name, cascade: false });
    } else {
      setConfirmDelete({ path, name, count });
    }
  }, [onCategoryAction]);

  // Collect all category paths with children (for expand/collapse all)
  const allParentPaths = useMemo(() => {
    const paths: string[] = [];
    const walk = (cats: Category[]) => {
      for (const c of cats) {
        if (c.children.length > 0) {
          paths.push(c.path);
          walk(c.children);
        }
      }
    };
    walk(categories);
    return paths;
  }, [categories]);

  const allCollapsed = allParentPaths.length > 0 && allParentPaths.every((p) => collapsed.has(p));

  const toggleExpandAll = useCallback(() => {
    if (allCollapsed) {
      setCollapsed(new Set());
    } else {
      setCollapsed(new Set(allParentPaths));
    }
  }, [allCollapsed, allParentPaths]);

  const totalCount = useMemo(
    () => categories.reduce((sum, c) => sum + c.count, 0),
    [categories],
  );

  return (
    <div className={styles.tree} onTouchStartCapture={() => { lastTouchRef.current = Date.now(); }}>
        <button
          className={`${styles.allBookmarks} ${allSelected ? styles.selected : ''}`}
          aria-current={allSelected ? 'true' : undefined}
          onClick={() => onSelect(null)}
        >
          <BookmarkIcon size={14} />
          <span className={styles.label}>All Bookmarks</span>
          <span className={styles.count}>{totalCount}</span>
        </button>
      <SidebarSection id="categories" title="Categories" activeLabel={selectedPath?.split('>').pop()}
        forceOpen={actionOpened}
        onCollapse={() => { setActionOpened(false); setSearchOpen(false); setSearchQuery(''); setAdding(false); }}
        actions={<>
          <button ref={searchToggleRef} type="button" className={styles.headerAction} aria-label="Find a category" title="Find a category"
            aria-expanded={searchOpen} onClick={() => { setActionOpened(true); setSearchOpen(!searchOpen); setSearchQuery(''); }}>
            <SearchIcon size={14} />
          </button>
          {onCategoryAction && <button type="button" className={styles.headerAction}
            aria-label="Add category" title="Add category" onClick={() => { setActionOpened(true); setAdding(true); }}><PlusIcon size={14} /></button>}
        {allParentPaths.length > 0 && (
          <button
            type="button"
            className={styles.headerAction}
            onClick={() => { setActionOpened(true); toggleExpandAll(); }}
            disabled={searching}
            aria-label={allCollapsed ? 'Expand all categories' : 'Collapse all categories'}
            title={allCollapsed ? 'Expand all' : 'Collapse all'}
          >
            <ChevronRightIcon size={12} className={allCollapsed ? undefined : styles.chevronOpen} />
          </button>
        )}
        </>}
      >
      {searchOpen && <div className={styles.search}>
        <input autoFocus aria-label="Find a category" placeholder="Find a category…" value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setSearchQuery(''); setSearchOpen(false); searchToggleRef.current?.focus(); } }} />
      </div>}
      <div ref={rootRef}>
        {visibleCategories.map((cat) => (
          <CategoryNode
            key={cat.path}
            category={cat}
            selectedPath={selectedPath}
            onSelect={onSelect}
            collapsed={collapsed}
            searching={searching}
            toggleCollapse={toggleCollapse}
            editingPath={editingPath}
            onStartEdit={setEditingPath}
            onRename={handleRename}
            onContextMenu={handleContextMenu}
            onRequestDelete={requestDelete}
            onCategoryAction={onCategoryAction}
            onRecategorize={onRecategorize}
          />
        ))}
      </div>
      {searchOpen && <div role="status">
        {searching && visibleCategories.length === 0 && <p className={styles.noMatches}>No matching categories</p>}
      </div>}

      {adding && (
        <div className={styles.addRow}>
          <InlineEdit
            defaultValue=""
            onConfirm={(name) => {
              setAdding(false);
              if (name.trim()) {
                onCategoryAction?.({ type: 'create', path: '', name: name.trim() });
              }
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}
      </SidebarSection>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          actions={[
            {
              label: 'Rename',
              onClick: () => setEditingPath(contextMenu.path),
            },
            {
              label: 'Move to root',
              onClick: () => onCategoryAction?.({ type: 'move-root', path: contextMenu.path, name: contextMenu.name }),
            },
            {
              label: 'Delete',
              danger: true,
              onClick: () => requestDelete(contextMenu.path, contextMenu.name, contextMenu.count),
            },
          ]}
        />
      )}

      {confirmDelete && (
        <Modal open onClose={() => setConfirmDelete(null)} title={`Delete "${confirmDelete.name}"?`}>
          <p className={styles.confirmText}>
            {`"${confirmDelete.name}" contains ${confirmDelete.count} bookmark${confirmDelete.count === 1 ? '' : 's'} (including subcategories).`}
          </p>
          <div className={styles.confirmActions}>
            <button
              type="button"
              className={styles.confirmDangerBtn}
              onClick={() => {
                onCategoryAction?.({ type: 'delete', path: confirmDelete.path, name: confirmDelete.name, cascade: true });
                setConfirmDelete(null);
              }}
            >
              {`Delete category and ${confirmDelete.count} bookmark${confirmDelete.count === 1 ? '' : 's'}`}
            </button>
            <button
              type="button"
              className={styles.confirmBtn}
              onClick={() => {
                onCategoryAction?.({ type: 'delete', path: confirmDelete.path, name: confirmDelete.name, cascade: false });
                setConfirmDelete(null);
              }}
            >
              Keep bookmarks — move to Uncategorized
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

interface CategoryNodeProps {
  category: Category;
  selectedPath: string | null;
  onSelect: (path: string | null) => void;
  collapsed: Set<string>;
  searching: boolean;
  toggleCollapse: (path: string) => void;
  editingPath: string | null;
  onStartEdit: (path: string | null) => void;
  onRename: (path: string, newName: string) => void;
  onContextMenu: (e: React.MouseEvent, path: string, name: string, count: number) => void;
  onRequestDelete: (path: string, name: string, count: number) => void;
  onCategoryAction?: (action: CategoryAction) => void;
  onRecategorize?: (bookmarkIds: string[], targetPath: string) => void;
}

function CategoryNode({
  category,
  selectedPath,
  onSelect,
  collapsed,
  searching,
  toggleCollapse,
  editingPath,
  onStartEdit,
  onRename,
  onContextMenu,
  onRequestDelete,
  onCategoryAction,
  onRecategorize,
}: CategoryNodeProps) {
  const [dragOver, setDragOver] = useState(false);
  const isCollapsed = !searching && collapsed.has(category.path);
  const isSelected = selectedPath === category.path;
  const hasChildren = category.children.length > 0;
  const isEditing = editingPath === category.path;
  const childrenRef = useRef<HTMLDivElement>(null);

  // Drag-reorder: child categories
  useEffect(() => {
    const el = childrenRef.current;
    if (!el || !onCategoryAction || searching) return;
    const sortable = createCategorySortable(el, onCategoryAction);
    return () => sortable.destroy();
  }, [onCategoryAction, isCollapsed, searching]);

  return (
    <div className={styles.node} data-path={category.path}>
      <div
        className={`${styles.item} ${isSelected ? styles.selected : ''} ${dragOver ? styles.dropTarget : ''}`}
        style={{ paddingLeft: `${category.depth * 12 + 8}px` }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('text/x-bookmark-ids')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDragOver(true);
          }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const raw = e.dataTransfer.getData('text/x-bookmark-ids');
          if (raw && onRecategorize) {
            try {
              const ids: string[] = JSON.parse(raw);
              onRecategorize(ids, category.path);
            } catch { /* invalid data */ }
          }
        }}
      >
        {hasChildren && (
          <button
            type="button"
            className={`${styles.chevron} ${isCollapsed ? '' : styles.chevronOpen}`}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            disabled={searching}
            onClick={() => toggleCollapse(category.path)}
          >
            <ChevronRightIcon size={12} />
          </button>
        )}
        {!hasChildren && <span className={styles.chevronSpacer} />}
        {isEditing ? (
          <InlineEdit
            defaultValue={category.name}
            onConfirm={(name) => onRename(category.path, name)}
            onCancel={() => onStartEdit(null)}
          />
        ) : (
          <button
            type="button"
            className={styles.itemBtn}
            aria-current={isSelected ? 'true' : undefined}
            title={category.name}
            onClick={() => onSelect(category.path)}
            onDoubleClick={() => onStartEdit(category.path)}
            onContextMenu={(e) => onContextMenu(e, category.path, category.name, category.count)}
          >
            <span className={styles.label}>{category.name}</span>
            <span className={styles.count}>{category.count}</span>
          </button>
        )}
        {isEditing && <span className={styles.count}>{category.count}</span>}
        {!isEditing && onCategoryAction && (
          <span className={styles.hoverActions}>
            <button
              type="button"
              className={styles.hoverBtn}
              aria-label="Rename category"
              onClick={() => onStartEdit(category.path)}
            >
              <EditIcon size={12} />
            </button>
            <button
              type="button"
              className={`${styles.hoverBtn} ${styles.hoverBtnDanger}`}
              aria-label="Delete category"
              onClick={() => onRequestDelete(category.path, category.name, category.count)}
            >
              <CloseIcon size={12} />
            </button>
          </span>
        )}
      </div>
      {hasChildren && !isCollapsed && (
        <div ref={childrenRef} className={styles.children} data-parent-path={category.path}>
          {category.children.map((child) => (
            <CategoryNode
              key={child.path}
              category={child}
              selectedPath={selectedPath}
              onSelect={onSelect}
              collapsed={collapsed}
              searching={searching}
              toggleCollapse={toggleCollapse}
              editingPath={editingPath}
              onStartEdit={onStartEdit}
              onRename={onRename}
              onContextMenu={onContextMenu}
              onRequestDelete={onRequestDelete}
              onCategoryAction={onCategoryAction}
              onRecategorize={onRecategorize}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InlineEdit({
  defaultValue,
  onConfirm,
  onCancel,
}: {
  defaultValue: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handledRef = useRef(false);
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handledRef.current = true;
      onConfirm(value);
    } else if (e.key === 'Escape') {
      handledRef.current = true;
      onCancel();
    }
  };

  return (
    <input
      ref={inputRef}
      className={styles.inlineInput}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={() => { if (!handledRef.current) onConfirm(value); }}
    />
  );
}
